import { computed, toValue } from 'vue';
import { formatDegreeProgram } from '@/js/core/constants/degree-programs';

const joinList = list => (list ?? []).filter(Boolean).join(', ');

/**
 * Derives the meta rows both profile surfaces render from a person shape.
 *
 * The profile page and the person panel show the same rows in the same order but
 * with different markup and stylesheets, so the derivation is shared here and the
 * markup is not.
 *
 * @param {(() => object)|object} person A shape from `utils/profile-shape.js`, as a
 *   getter, ref, or plain object.
 */
export function usePersonMeta(person) {
  const shape = computed(() => toValue(person) ?? {});

  const departmentText = computed(() => joinList(shape.value.departments));
  const researchAreasText = computed(() => joinList(shape.value.researchAreas));
  const titlesText = computed(() => joinList(shape.value.titles));
  const officesText = computed(() => joinList(shape.value.offices));

  // Render the abbreviation with its full name, then the graduation year.
  const programsText = computed(() =>
    [formatDegreeProgram(shape.value.program), shape.value.programYear].filter(Boolean).join(' ')
  );

  const hasDirectoryBox = computed(
    () =>
      Boolean(departmentText.value) ||
      Boolean(researchAreasText.value) ||
      Boolean(titlesText.value) ||
      Boolean(programsText.value)
  );

  const hasContactBox = computed(
    () =>
      Boolean(officesText.value) ||
      Boolean(shape.value.officeAddress) ||
      Boolean(shape.value.email) ||
      Boolean(shape.value.phone)
  );

  const phoneHref = computed(
    () => `tel:${String(shape.value.phone ?? '').replace(/[^+0-9]/g, '')}`
  );

  return {
    departmentText,
    researchAreasText,
    titlesText,
    officesText,
    programsText,
    hasDirectoryBox,
    hasContactBox,
    phoneHref,
  };
}
